import { Tag } from 'lucide-react';
import { EmptyState } from '@/shared/components/empty-state';
import { Card } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { formatDate, formatTime, onlyDigits } from '@/shared/lib/format';
import { formatCooldownRemaining } from '@/features/leads/model/card-resend';
import { useLeadCampaignEnrollments, type LeadCampaignEnrollment } from '../api/use-loyalty-admin';

function dateTime(value: string): string {
  return `${formatDate(value)} ${formatTime(value)}`;
}

function activationWindow(e: LeadCampaignEnrollment): string {
  if (!e.live) return `Expirou em ${dateTime(e.expiresAt)}`;
  return `Expira em ${formatCooldownRemaining(new Date(e.expiresAt).getTime() - Date.now())}`;
}

function period(e: LeadCampaignEnrollment): string | null {
  if (!e.startDate && !e.endDate) return null;
  return e.endDate
    ? `Vigência ${formatDate(e.startDate)} a ${formatDate(e.endDate)}`
    : `Vigência desde ${formatDate(e.startDate)}`;
}

/**
 * Aba "Campanhas" no detalhe do lead: o que o cliente ativou no app do
 * parceiro. A ativação vale 24 horas e cada novo "Ativar" reabre a janela.
 */
export function LeadLoyaltyTab({ idPassbook, uniqueId }: { idPassbook: string; uniqueId?: string }) {
  const ssn = onlyDigits(uniqueId ?? '');
  const { data = [], isLoading } = useLeadCampaignEnrollments(idPassbook, ssn);

  if (!ssn) {
    return (
      <Card>
        <EmptyState icon={Tag} title="Lead sem CPF" description="A ativação de campanha é feita pelo CPF." />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-2xl" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <EmptyState icon={Tag} title="Nenhuma campanha ativada" description="Ativações feitas no app aparecem aqui." />
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {data.map((e) => {
        const vigencia = period(e);
        return (
          <Card key={e.campaignId} className="flex items-start justify-between gap-3 p-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground tabular-nums">#{e.campaignId}</span>
                <span className="font-medium">{e.name ?? 'Campanha removida'}</span>
                {!e.campaignActive && e.name ? <Badge variant="outline">desligada</Badge> : null}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Ativada em {dateTime(e.enabledAt)} · {activationWindow(e)}
              </p>
              {vigencia ? <p className="mt-1 text-xs text-muted-foreground">{vigencia}</p> : null}
            </div>
            <div className="shrink-0">
              {e.live && e.locked ? (
                <Badge variant="info">Em uso no caixa</Badge>
              ) : e.live ? (
                <Badge variant="success">Ativa</Badge>
              ) : (
                <Badge variant="secondary">Expirada</Badge>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
